import { MapPin } from 'lucide-react'
import { useMemo } from 'react'
import type { GraphViewNode } from '@/types/graph-view'
import { cn } from '@/utils/cn'
import { extractGeoPoint } from '@/utils/geo'

interface MapMiniPreviewProps {
  nodes: GraphViewNode[]
  className?: string
}

const VB_W = 200
const VB_H = 138

/**
 * Mini map preview — extracts (lat, lon) pairs from graph-view node properties
 * and plots them on a stylised graticule. Falls back to an empty state when no
 * geo-coded nodes are present.
 */
export function MapMiniPreview({ nodes, className }: MapMiniPreviewProps) {
  const { points, total } = useMemo(() => extractPoints(nodes), [nodes])

  return (
    <div
      data-empty={total > 0 ? undefined : ''}
      className={cn(
        'relative overflow-hidden rounded-lg border border-border/40 bg-muted/30',
        className
      )}
    >
      <div className="absolute left-2 top-2 z-10 flex items-center gap-1 text-3xs font-medium uppercase tracking-wider text-muted-foreground">
        <MapPin className="size-3" />
        Map
      </div>
      {total > 0 ? (
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="block h-full w-full"
          preserveAspectRatio="none"
        >
          <g className="text-border" stroke="currentColor" strokeWidth={0.4} opacity={0.65}>
            {[0.25, 0.5, 0.75].map(t => (
              <line key={`h-${t}`} x1={0} x2={VB_W} y1={VB_H * t} y2={VB_H * t} />
            ))}
            {[0.25, 0.5, 0.75].map(t => (
              <line key={`v-${t}`} x1={VB_W * t} x2={VB_W * t} y1={0} y2={VB_H} />
            ))}
          </g>
          <g className="text-success">
            {points.map((p, i) => (
              <g key={i}>
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={4.5}
                  fill="currentColor"
                  fillOpacity={0.15}
                  stroke="none"
                />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={2.2}
                  fill="currentColor"
                  fillOpacity={0.95}
                  stroke="currentColor"
                  strokeWidth={0.6}
                />
              </g>
            ))}
          </g>
        </svg>
      ) : (
        <div className="flex h-full w-full items-center justify-center text-2xs text-muted-foreground">
          No location data
        </div>
      )}
      <div className="absolute bottom-1.5 right-2 z-10 rounded bg-background/70 px-1.5 py-0.5 font-mono text-3xs text-muted-foreground backdrop-blur">
        {total} points
      </div>
    </div>
  )
}

interface Pt {
  x: number
  y: number
}

function extractPoints(nodes: GraphViewNode[]): { points: Pt[]; total: number } {
  const raw: { lat: number; lon: number }[] = []
  for (const node of nodes) {
    const point = extractGeoPoint(node.properties)
    if (point) raw.push(point)
  }
  if (raw.length === 0) return { points: [], total: 0 }

  const lats = raw.map(p => p.lat)
  const lons = raw.map(p => p.lon)
  const minLat = Math.min(...lats)
  const maxLat = Math.max(...lats)
  const minLon = Math.min(...lons)
  const maxLon = Math.max(...lons)
  const spanLat = Math.max(maxLat - minLat, 0.0001)
  const spanLon = Math.max(maxLon - minLon, 0.0001)
  const padX = 14
  const padY = 18
  const usableW = VB_W - padX * 2
  const usableH = VB_H - padY * 2

  const points = raw.map(p => ({
    x: padX + ((p.lon - minLon) / spanLon) * usableW,
    // Mirror Y so north appears at the top of the SVG.
    y: padY + (1 - (p.lat - minLat) / spanLat) * usableH,
  }))
  return { points, total: raw.length }
}
